import React, { useState, useEffect } from "react";
import { useLogApi } from "../../api/LogApi";
import { useFontenehus } from "../../context/FontenehusContext";
import { handleApiError } from "../../utils/handleApiError";

export interface LogItem {
  id: number;
  username: string;
  action: string;
  timestamp: string;
  displayName: String;
  logType: string;
  date: string;
  objectName: string;
  actionBy: string;
}

const ActivityLog: React.FC = () => {
  const [showLog, setShowLog] = useState(false);
  const [openLogs, setOpenLogs] = useState<Record<number, boolean>>({});
  const [logs, setLogs] = useState<LogItem[]>([]);
  const { selectedFontenehus } = useFontenehus();
  const { getLogsByFontenehusId } = useLogApi();

  // Date filter state
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  // Load last 7 days on first render
  useEffect(() => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    setFromDate(sevenDaysAgo.toISOString().split("T")[0]);
    setToDate(today.toISOString().split("T")[0]);
  }, []);

  // Reload logs whenever fontenehus or date range changes
  useEffect(() => {
    if (!selectedFontenehus) return;

    const loadLogs = async () => {
      if (!selectedFontenehus) return;

      try {
        // ✅ Pass fromDate/toDate to backend
        const logResult = await getLogsByFontenehusId(
          selectedFontenehus.id,
          fromDate,
          toDate,
        );
        // Defensive check: ensure data is array
        setLogs(Array.isArray(logResult.data?.data) ? logResult.data.data : []);
      } catch (err: any) {
        handleApiError(err, "Feil ved lasting av logg.");
        setLogs([]); // fallback to empty array
      }
    };

    loadLogs();
  }, [selectedFontenehus, fromDate, toDate, getLogsByFontenehusId]);

  const toggleLog = (id: number) => {
    setOpenLogs((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Quick date range buttons
  const setLast7Days = () => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    setFromDate(sevenDaysAgo.toISOString().split("T")[0]);
    setToDate(today.toISOString().split("T")[0]);
  };

  const setLast30Days = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    setFromDate(thirtyDaysAgo.toISOString().split("T")[0]);
    setToDate(today.toISOString().split("T")[0]);
  };

  return (
    <section className="bg-white shadow p-5 rounded-xl">
      {/* OUTER DROPDOWN BUTTON */}
      <button
        onClick={() => setShowLog((s) => !s)}
        className="w-full flex justify-between items-center text-xl font-semibold text-orangeBrand"
      >
        <span>Aktivitetslogg</span>
        <span className="text-gray-500">{showLog ? "▲" : "▼"}</span>
      </button>

      <div
        className={`transition-all duration-300 overflow-hidden ${
          showLog ? "max-h-[3000px] mt-4" : "max-h-0"
        }`}
      >
        {/* DATE FILTER */}
        <div className="bg-orange-100 p-3 rounded-md mb-4 grid grid-cols-2 gap-3">
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700">
              Fra dato
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="border px-3 py-2 rounded"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700">
              Til dato
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="border px-3 py-2 rounded"
            />
          </div>
        </div>

        {/* QUICK BUTTONS */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={setLast7Days}
            className="bg-orangeBrand text-white px-3 py-1 rounded hover:bg-orangeHover"
          >
            Siste 7 dager
          </button>
          <button
            onClick={setLast30Days}
            className="bg-orangeBrand text-white px-3 py-1 rounded hover:bg-orangeHover"
          >
            Siste 30 dager
          </button>
        </div>

        {/* LOG LIST */}
        <ul className="flex flex-col gap-2">
          {logs.map((log) => {
            let parsedChanges: Record<string, any> | null = null;

            try {
              parsedChanges = log.action ? JSON.parse(log.action) : null;
            } catch {
              parsedChanges = null;
            }

            const isOpen = openLogs[log.id] ?? false;

            return (
              <li
                key={log.id}
                className="bg-orange-50 rounded-md shadow-sm p-3"
              >
                {/* LOG HEADER */}
                <button
                  className="w-full flex justify-between items-center font-medium text-left"
                  onClick={() => toggleLog(log.id)}
                >
                  <span>
                    {log.username} —{" "}
                    <span className="text-gray-500">
                      {log.logType} | {log.objectName} |{" "}
                      {new Date(log.date).toLocaleString()}
                    </span>
                  </span>
                  <span className="text-gray-500">{isOpen ? "▲" : "▼"}</span>
                </button>

                {/* LOG DETAILS */}
                <div
                  className={`transition-all duration-300 overflow-hidden ${
                    isOpen ? "max-h-[1000px] mt-3" : "max-h-0"
                  }`}
                >
                  {parsedChanges ? (
                    <ul className="list-disc list-inside text-sm text-gray-700">
                      {Object.entries(parsedChanges).map(([key, value]) => (
                        <li key={key}>
                          <strong>{key}:</strong>{" "}
                          {"Before" in value && "After" in value ? (
                            <>
                              <span className="line-through text-red-500">
                                {Array.isArray(value.Before)
                                  ? value.Before.map((v: any) =>
                                      v.Name
                                        ? v.Quantity != null
                                          ? `${v.Name} (${v.Quantity}${
                                              v.Unit ? " " + v.Unit : ""
                                            })`
                                          : v.Name
                                        : JSON.stringify(v),
                                    ).join(", ")
                                  : value.Before}
                              </span>{" "}
                              →{" "}
                              <span className="text-green-600">
                                {Array.isArray(value.After)
                                  ? value.After.map((v: any) =>
                                      v.Name
                                        ? v.Quantity != null
                                          ? `${v.Name} (${v.Quantity}${
                                              v.Unit ? " " + v.Unit : ""
                                            })`
                                          : v.Name
                                        : JSON.stringify(v),
                                    ).join(", ")
                                  : value.After}
                              </span>
                            </>
                          ) : (
                            JSON.stringify(value)
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm">{log.action}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">{log.timestamp}</p>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export default ActivityLog;
